import React, { useState, useCallback } from 'react';
import { Loader2, Copy, Check, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button, Textarea, Label, Card, CardHeader, CardTitle, CardDescription, CardContent } from '@clinic-os/ui';
import { VoiceRecorder } from './VoiceRecorder';
import { useProcessConsultation, type ProcessConsultationResult, type ProcessConsultationError } from '../../hooks/useAIDocumentation';
import type { AICodeSuggestion } from '../../services/aiService';

type ProcessingStep = 'idle' | 'recording' | 'processing' | 'complete';

interface AIDocumentationProps {
  patientContext?: string;
}

export function AIDocumentation({ patientContext }: AIDocumentationProps) {
  const [transcript, setTranscript] = useState('');
  const [notes, setNotes] = useState('');
  const [editedNotes, setEditedNotes] = useState('');
  const [suggestions, setSuggestions] = useState<AICodeSuggestion[]>([]);
  const [copied, setCopied] = useState(false);
  const [currentStep, setCurrentStep] = useState<ProcessingStep>('idle');
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);

  const processConsultation = useProcessConsultation({
    onSuccess: (data: ProcessConsultationResult) => {
      setTranscript(data.transcript);
      setNotes(data.soapNote.fullText);
      setEditedNotes(data.soapNote.fullText);
      setSuggestions(data.codes);
      setCurrentStep('complete');
      setApiKeyError(null);
    },
    onError: (error: ProcessConsultationError) => {
      if (error.code === 'MISSING_API_KEY') {
        setApiKeyError('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your environment variables.');
      } else {
        setApiKeyError(error.message || 'An error occurred during processing');
      }
      setCurrentStep('idle');
    },
  });

  const handleRecordingComplete = useCallback(
    (audioBlob: Blob) => {
      setCurrentStep('processing');
      processConsultation.mutate({ audioBlob, patientContext });
    },
    [processConsultation, patientContext]
  );

  const handleRecordingStart = useCallback(() => {
    setCurrentStep('recording');
    setApiKeyError(null);
  }, []);

  const handleRecordingStop = useCallback(() => {
    // Recording stopped, waiting for processing to complete
  }, []);

  const copyNotes = () => {
    navigator.clipboard.writeText(editedNotes);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetSession = () => {
    setTranscript('');
    setNotes('');
    setEditedNotes('');
    setSuggestions([]);
    setCurrentStep('idle');
    setApiKeyError(null);
  };

  const copyCode = (code: AICodeSuggestion) => {
    navigator.clipboard.writeText(code.code);
  };

  // Check if API key is configured on mount
  React.useEffect(() => {
    const apiKey = import.meta.env['VITE_GEMINI_API_KEY'] as string | undefined;
    if (!apiKey) {
      setApiKeyError('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your environment variables to enable AI documentation.');
    }
  }, []);

  const getStepStatus = () => {
    switch (currentStep) {
      case 'idle':
        return { step: 0, label: 'Ready' };
      case 'recording':
        return { step: 1, label: 'Recording' };
      case 'processing':
        return { step: 2, label: 'Processing' };
      case 'complete':
        return { step: 3, label: 'Complete' };
    }
  };

  const { step: currentStepNum, label: stepLabel } = getStepStatus();

  const steps = [
    { num: 1, label: 'Record' },
    { num: 2, label: 'Process' },
    { num: 3, label: 'Review' },
  ];

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.num}>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      currentStepNum >= step.num
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {currentStepNum > step.num ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      step.num
                    )}
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      currentStepNum >= step.num ? 'text-slate-900' : 'text-slate-500'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      currentStepNum > step.num ? 'bg-blue-600' : 'bg-slate-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          {currentStep !== 'idle' && (
            <p className="text-xs text-slate-500 mt-3 text-center">
              {currentStep === 'recording' && 'Speak clearly into your microphone...'}
              {currentStep === 'processing' && 'Transcribing audio and generating SOAP note...'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Recording Section */}
      <Card>
        <CardHeader>
          <CardTitle>Voice Input</CardTitle>
          <CardDescription>
            Press Start Recording and talk through the consultation. When you stop, MedFlow drafts a SOAP note and suggests ICD-10 and CPT codes for your review.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <VoiceRecorder
            onRecordingComplete={handleRecordingComplete}
            onRecordingStart={handleRecordingStart}
            onRecordingStop={handleRecordingStop}
            disabled={currentStep === 'processing' || !!apiKeyError}
          />

          {apiKeyError && (
            <p className="flex items-start gap-2 text-sm text-slate-600" role="status">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-700" aria-hidden="true" />
              <span>
                <span className="font-medium text-slate-900">Configuration Required.</span> {apiKeyError}
              </span>
            </p>
          )}

          {/* Loading state during processing */}
          {currentStep === 'processing' && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <div>
                <p className="text-sm font-medium text-blue-900">Processing consultation...</p>
                <p className="text-xs text-blue-700">This may take a few seconds</p>
              </div>
            </div>
          )}

          {/* Transcript preview */}
          {transcript && (
            <div className="space-y-2">
              <Label className="text-slate-700">Transcript</Label>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-700 italic">"{transcript}"</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generated Notes */}
      {notes && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Generated SOAP Note</CardTitle>
              <Button variant="ghost" size="sm" onClick={copyNotes} className="gap-1.5">
                {copied ? (
                  <Check className="w-4 h-4 text-green-700" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              className="min-h-[200px] font-mono text-sm"
              placeholder="Generated notes will appear here..."
            />
            <p className="mt-2 text-xs text-slate-500">
              Review and edit the generated note before saving
            </p>
          </CardContent>
        </Card>
      )}

      {/* Code Suggestions */}
      {suggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Suggested Codes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {suggestions.map((s, index) => (
                <div
                  key={`${s.type}-${s.code}-${index}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-200 transition-colors cursor-pointer"
                  onClick={() => copyCode(s)}
                  title="Click to copy code"
                >
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium ${
                      s.type === 'ICD-10'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {s.type}
                  </span>
                  <span className="font-mono font-medium text-slate-900">{s.code}</span>
                  <span className="text-sm text-slate-600">{s.description}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Codes are AI-suggested — always verify before submitting claims. Click a code to copy it.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Reset Button */}
      {currentStep === 'complete' && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={resetSession} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Start New Session
          </Button>
        </div>
      )}
    </div>
  );
}

export default AIDocumentation;
